import { Button, Col, Input, message, Row, Space, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { API_METHODS, BLANK_VALUE } from "../../../constants";
import { displayFormattedDate, formatCurrency, isEmpty } from "../../../generalFunctions";
import useApi from "../../../hooks/useApi";
import PrintPdfModal from "../../common/PrintPdfModal";
import TableComponent from "../../common/table/TableComponent";
import AddCreditDebitNote from "./AddCreditDebitNote";
import {
  EditOutlined,
  EyeOutlined,
  PrinterOutlined,
} from "@ant-design/icons";

function CreditNoteTable() {
    const columns = [
        {
            title: "CN Number",
            dataIndex: "id",
            width: "100px"
        },
        {
            title: "Account",
            dataIndex: "accountName",
        },
        {
            title: "Date",
            dataIndex: "date",
            render: (text) => displayFormattedDate(text)
        },
        {
            title: "Notes",
            dataIndex: "notes",
            width: "500px"
        },
        {
            title: "Total Amount",
            dataIndex: "totalAmount",
            render: (text) => formatCurrency(text)
        },
        {
            title: "Action",
            key: "action",

            render: (text, record) => (
                <Space>
                    <Button size="small" onClick={() => editRecord(record)}>
                        <EditOutlined />
                    </Button>
                    <Button size="small" onClick={() => editRecord(record, true)}>
                        <EyeOutlined />
                    </Button>
                    <Button size="small" onClick={() => handlePrint(record)}>
                        <PrinterOutlined />
                    </Button>
                </Space>
            ),
        },
    ];

    const [responseData, getRequest] = useApi(
        "/creditNote",
        null,
        API_METHODS.GET
    );
    const [visible, setVisible] = useState(false);
    const [visibleEdit, setVisibleEdit] = useState(false);
    const [visiblePrint, setVisiblePrint] = useState(false);
    const [isViewOnly, setIsViewOnly] = useState(false);
    const [tableData, setTableData] = useState();
    const [selectedRecord, setSelectedRecord] = useState();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [singleGetResponse, singleGetRequest] = useApi(`/creditNote/`, null, API_METHODS.GET);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        getRequest(null, API_METHODS.GET, `/creditNote?pageNumber=${1}`);
    }, []);

    useEffect(() => {
        const data = responseData.data !== BLANK_VALUE ? responseData.data : [];
        setTableData(data);
        setTotalRecords(data && data.length > 0 ? data[0].count : 0);
    }, [responseData]);

    const handleAddCreditNote = (isUpdated) => {
        const flag = visible ? false : true;
        setVisible(flag);
        if (isUpdated === true) {
            getRequest(null, API_METHODS.GET, `/creditNote?pageNumber=${1}`);
        }
    };

    const handleEditCreditNote = (isUpdated) => {
        const flag = visibleEdit ? false : true;
        !flag && setSelectedRecord(null);
        setVisibleEdit(flag);
        if (isUpdated === true) {
            getRequest(null, API_METHODS.GET, `/creditNote?pageNumber=${1}`);
        }
    };

    const editRecord = (record, isViewOnly = false) => {
        setIsViewOnly(isViewOnly);
        setVisibleEdit(true);
        setSelectedRecord(record);
    };

    const handlePrint = (record) => {
        const flag = visiblePrint ? false : true;
        record && setSelectedRecord(record);
        setVisiblePrint(flag);
    };

    const onCreditNoteSearch = (id) => {
        if (!isEmpty(id))
            singleGetRequest(null, API_METHODS.GET, `/creditNote/${id}`)
    }

    useEffect(() => {
        if (singleGetResponse && !isEmpty(singleGetResponse.data) && !singleGetResponse.isLoading) {
            const { data } = singleGetResponse;
            setIsViewOnly(false);
            setVisibleEdit(true);
            setSelectedRecord(data[0]);
        }
        else if (singleGetResponse && singleGetResponse.data === BLANK_VALUE && !singleGetResponse.isLoading) {
            message.error("Credit Note not found")
        }
    }, [singleGetResponse]);

    const onRowSelectChange = (newSelectedRowKeys, records) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(records);
    }

    const rowSelection = {
        selectedRowKeys,
        type: "checkbox",
        onChange: onRowSelectChange
    };

    const onPaginationChange = (page) => {
        getRequest(null, API_METHODS.GET, `/creditNote?pageNumber=${page.current}`);
        setCurrentPage(page.current);
    };

    return (
        <>
            <Row>
                <Col span={6}>
                    <Typography.Title level={5}>Credit Notes</Typography.Title>
                </Col>
                <Col span={18}>
                    <Space style={{ marginBottom: 10, float: "right" }}>
                        <Input.Search
                            prefix="CN-"
                            placeholder="Credit Note Number"
                            allowClear
                            enterButton="Search"
                            onSearch={onCreditNoteSearch}
                        />
                        <Button type="primary" onClick={handleAddCreditNote}>
                            Credit Note
                        </Button>
                        <Button type="primary" onClick={handlePrint} disabled={selectedRows.length === 0}>
                            Print Selected CN's
                        </Button>
                    </Space>
                </Col>
            </Row>
            <TableComponent
                columns={columns}
                tableData={tableData}
                loading={responseData.isLoading}
                rowSelection={rowSelection}
                onPaginationChange={onPaginationChange}
                total={totalRecords}
                current={currentPage}
            />

            {visible && (
                <AddCreditDebitNote visible={visible} close={handleAddCreditNote} />
            )}

            {visibleEdit && (
                <AddCreditDebitNote
                    visible={visibleEdit}
                    selectedRecord={selectedRecord}
                    close={handleEditCreditNote}
                    isViewOnly={isViewOnly}
                />
            )}

            {visiblePrint && (
                <PrintPdfModal
                    visible={visiblePrint}
                    data={selectedRows.length !== 0 ? selectedRows : [selectedRecord]}
                    title={"Credit Note"}
                    close={handlePrint}
                    pdfType="creditNote"
                />
            )}
        </>
    );
}

export default CreditNoteTable;
